import React, { useState, useEffect } from 'react';
import { Plan, PlanCreate } from '../types';
import PlanFeatureAssignment from '../components/PlanFeatureAssignment';
import FeatureManager from '../components/FeatureManager';
import PlanForm from '../components/PlanForm';
import Modal from '../components/Modal';

const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showFeatureManager, setShowFeatureManager] = useState(false);
  const [updatingPlanId, setUpdatingPlanId] = useState<number | null>(null);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false); // New state for inactive filter

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Using the existing plans API endpoint with relative URL (proxy will handle routing)
      let url = '/api/v1/plans/';
      
      // Add query parameters for filtering
      const params = new URLSearchParams();
      if (!showInactive) {
        params.append('is_active', 'true');
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleManageFeatures = (plan: Plan) => {
    setSelectedPlan(plan);
  };

  const handleCloseFeatureAssignment = () => {
    setSelectedPlan(null);
  };

  const handleCloseFeatureManager = () => {
    setShowFeatureManager(false);
  };

  const handleTogglePlanStatus = async (plan: Plan) => {
    const nextStatus = !plan.is_active;
    const actionLabel = nextStatus ? 'enable' : 'disable';

    if (!window.confirm(`Do you want to ${actionLabel} "${plan.name}"?`)) {
      return;
    }

    try {
      setUpdatingPlanId(plan.plan_id);
      setError(null);

      const response = await fetch(`/api/v1/plans/${plan.plan_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: nextStatus }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await loadPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update plan status');
    } finally {
      setUpdatingPlanId(null);
    }
  };

  const handleCreatePlan = async (planData: PlanCreate) => {
    try {
      setError(null);
      
      const response = await fetch('/api/v1/plans/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await loadPlans();
      setShowCreatePlanModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create plan');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Plans & Features</h1>
        <div className="header-actions">
          <button 
            onClick={() => setShowFeatureManager(true)}
            className="btn btn-secondary"
          >
            Manage Features
          </button>
          <button 
            onClick={loadPlans}
            className="btn btn-secondary"
            disabled={loading}
          >
            Refresh
          </button>
          <button 
            onClick={() => setShowCreatePlanModal(true)}
            className="btn btn-primary"
          >
            + Add Plan
          </button>
        </div>
      </div>
      
      <div className="filter-bar">
        <label>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Show inactive plans
        </label>
      </div>

      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="btn btn-sm">×</button>
        </div>
      )}

      <div className="plans-grid">
        {plans.map(plan => (
          <div key={plan.plan_id} className={`plan-card ${!plan.is_active ? 'inactive-card' : ''}`}>
            <div className="plan-header">
              <h3 className="plan-name">{plan.name}</h3>
              <span className={`plan-status ${plan.is_active ? 'active' : 'inactive'}`}>
                {plan.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            {plan.description && (
              <p className="plan-description">{plan.description}</p>
            )}
            
            <div className="plan-details">
              <div className="detail-item">
                <span className="detail-label">Valid From:</span>
                <span className="detail-value">
                  {new Date(plan.valid_from).toLocaleDateString()}
                </span>
              </div>
              {plan.valid_to && (
                <div className="detail-item">
                  <span className="detail-label">Valid To:</span>
                  <span className="detail-value">
                    {new Date(plan.valid_to).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Created:</span>
                <span className="detail-value">
                  {new Date(plan.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="plan-actions">
              <button
                onClick={() => handleManageFeatures(plan)}
                className="btn btn-primary"
              >
                Manage Features
              </button>
              <button
                onClick={() => handleTogglePlanStatus(plan)}
                className={`btn ${plan.is_active ? 'btn-danger' : 'btn-success'}`}
                disabled={updatingPlanId === plan.plan_id}
              >
                {updatingPlanId === plan.plan_id
                  ? 'Saving...'
                  : plan.is_active
                    ? 'Disable Plan'
                    : 'Enable Plan'}
              </button>
            </div>
          </div>
        ))}

        {plans.length === 0 && !loading && (
          <div className="empty-state">
            <h3>No Plans Found</h3>
            <p>There are no plans configured in the system.</p>
          </div>
        )}
      </div>

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <Modal
          title="Create Plan"
          onClose={() => setShowCreatePlanModal(false)}
        >
          <PlanForm
            initialData={null}
            onSubmit={handleCreatePlan}
            onCancel={() => setShowCreatePlanModal(false)}
          />
        </Modal>
      )}

      {/* Feature Assignment Modal */}
      {selectedPlan && (
        <PlanFeatureAssignment
          plan={selectedPlan}
          onClose={handleCloseFeatureAssignment}
        />
      )}

      {/* Feature Manager Modal */}
      {showFeatureManager && (
        <FeatureManager onClose={handleCloseFeatureManager} />
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .page-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .filter-bar {
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .filter-bar label {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
          color: #666;
          cursor: pointer;
        }

        .filter-bar input[type="checkbox"] {
          cursor: pointer;
        }

        .page-header h1 {
          margin: 0;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .plan-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 1.5rem;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          transition: box-shadow 0.2s, opacity 0.2s;
        }

        .plan-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        .plan-card.inactive-card {
          opacity: 0.7;
          border-color: #ccc;
          background-color: #f8f9fa;
        }

        .plan-card.inactive-card:hover {
          opacity: 0.85;
        }

        .plan-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .plan-name {
          margin: 0;
          color: #333;
          font-size: 1.25rem;
        }

        .plan-status {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .plan-status.active {
          background-color: #d4edda;
          color: #155724;
        }

        .plan-status.inactive {
          background-color: #f8d7da;
          color: #721c24;
        }

        .plan-description {
          color: #666;
          margin-bottom: 1rem;
          line-height: 1.5;
        }

        .plan-details {
          margin-bottom: 1.5rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .detail-label {
          font-weight: 500;
          color: #666;
        }

        .detail-value {
          color: #333;
        }

        .plan-actions {
          display: flex;
          gap: 0.5rem;
        }

        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        .empty-state h3 {
          margin-bottom: 1rem;
          color: #333;
        }

        .loading-container {
          text-align: center;
          padding: 3rem;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .alert {
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .alert-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          display: inline-block;
          transition: background-color 0.2s;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        `
      }} />
    </div>
  );
};

export default PlansPage;
